"use client";

import { Collapse, Drawer } from "@mui/material";
import React, { useState, useEffect, useRef } from "react"
import { Outlet, useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
  Button,
} from "@mui/material"
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon,
  CardMembership as CardIcon,
  AccountCircle as AccountIcon,
  ChevronLeft as ChevronLeftIcon,
} from "@mui/icons-material"
import { useAuth } from "../../hooks/useAuth"
import { useTeacher } from "../../hooks/useTeacher"
import { useStudents } from "../../hooks/useStudents"
import { useSchools } from "../../hooks/useSchools"
import { useSchoolIdForAdmin } from "../../hooks/useSchoolIdForAdmin"
import { DRAWER_WIDTH } from "./Layout";
import { useTranslation } from "react-i18next";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useError } from "../../contexts/ErrorContext";
import SubjectIcon from '@mui/icons-material/Subject';
import HouseIcon from '@mui/icons-material/House';
import ListAltIcon from '@mui/icons-material/ListAlt';
import GroupsIcon from '@mui/icons-material/Groups';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ArticleIcon from '@mui/icons-material/Article';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ClassIcon from '@mui/icons-material/Class';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DateRangeIcon from '@mui/icons-material/DateRange';
import HistoryIcon from '@mui/icons-material/History';
import Description from '@mui/icons-material/Description';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import SmsIcon from '@mui/icons-material/Sms';
import { useCurrentStudent } from "../../hooks/useCurrentStudent";
import {
  getMarksheetsPageByClassKeyAndSessionId,
  useMarkSheets,
} from "../../hooks/useMarkSheets";
import { useTdData } from "../../hooks/useTdData";
import { getStudentData } from "../../utils/storage";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const apiBaseUrl = process.env.REACT_APP_API_URL; // e.g., http://localhost:8080/api

interface AppSideBarProps {
  mobileOpen?: boolean;
  handleDrawerToggle?: () => void;
  /** Same class/session as Layout header selectors (used for marksheet prefetch). */
  selectedClassId?: number;
  selectedSessionId?: number;
}

export default function AppSideBar(props: AppSideBarProps = {}) {
  const {
    mobileOpen: externalMobileOpen,
    handleDrawerToggle: externalHandleDrawerToggle,
    selectedClassId: selectedClassIdFromLayout,
    selectedSessionId: selectedSessionIdFromLayout,
  } = props;
  const [resultSheetOpen, setResultSheetOpen] = useState(false)
  const [promotionOpen, setPromotionOpen] = useState(false)
  const [resultsOpen, setResultsOpen] = useState(false)
  const [accountsOpen, setAccountsOpen] = useState(false)
  const [certificateOpen, setCertificateOpen] = useState(false)
  const [logoError, setLogoError] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [internalMobileOpen, setInternalMobileOpen] = useState(true)
  const { t } = useTranslation();
  const { selectedClass, selectedClassName, selectedSession } = useSessionClass();
  const queryClient = useQueryClient();

  // Use external state if provided, otherwise use internal state
  const mobileOpen = externalMobileOpen !== undefined ? externalMobileOpen : internalMobileOpen;

  const handleDrawerToggle = externalHandleDrawerToggle || (() => {
    setInternalMobileOpen(!internalMobileOpen)
  })

  const { user, isLoading: authLoading, isTeacher, isStudent, isSchoolAdmin, isAdmin } = useAuth();
  const { showSuccess, showError } = useError();

  // Check if user has APP_USER role
  const isAppUser = user?.roles?.includes('APP_USER');
  const { useSchoolLogoInfo, useSchoolById, updateSchoolMutation } = useSchools(authLoading || !user, { enableQuery: false });
  const schoolId = user?.schoolId ?? undefined;

  const { data: logoInfo, isLoading: logoLoading } = useSchoolLogoInfo(schoolId);
  const { data: schoolData } = useSchoolById(schoolId);

  useEffect(() => {
    setLogoError(false);
  }, [logoInfo?.fileName]);

  // ---------- Student promotion visibility logic ----------
  const { studentData, isStudent: isStudentRole } = useCurrentStudent();
  const { useMarksheetByStudentId } = useMarkSheets(false, true);
  const { getTcByStudentCode } = useTdData();

  const { data: studentMarksheets } = useMarksheetByStudentId(
    studentData?.id ?? null,
    !!studentData?.id && isStudentRole
  );

  // Get studentCode from student data or storage
  const studentCode = studentData?.studentCode || getStudentData()?.studentCode;
  const currentClassId = studentData?.schoolClassId;
  const currentSessionId = studentData?.sessionId;

  // Fetch TC data for the student when they log in
  const { data: studentTcData } = useQuery({
    queryKey: ["studentTcData", studentCode, currentSessionId, currentClassId],
    queryFn: async () => {
      if (!studentCode || !currentSessionId || !currentClassId) {
        return null;
      }
      try {
        const tcData = await getTcByStudentCode(
          currentSessionId,
          currentClassId,
          studentCode
        );
        return tcData;
      } catch (error: any) {
        // If 404 or "not found" error, student doesn't have TC data
        if (error?.response?.status === 404 ||
          error?.response?.data?.message?.toLowerCase().includes("not found") ||
          error?.response?.data?.message?.toLowerCase().includes("not created")) {
          return null;
        }
        // For other errors, return null (assume no TC data)
        return null;
      }
    },
    enabled: !!studentCode && !!currentSessionId && !!currentClassId && isStudentRole,
    staleTime: 0, // Always fetch fresh data
    cacheTime: 0, // Don't cache old data
  });

  const shouldShowStudentPromotion = React.useMemo(() => {
    if (!isStudentRole || !studentData || !studentMarksheets) return false;

    // If TC data exists, don't show promotion
    if (studentTcData !== null && studentTcData !== undefined) {
      return false;
    }

    const currentClassId = studentData.schoolClassId;
    const currentSessionId = studentData.sessionId;

    if (!currentClassId || !currentSessionId) return false;

    const relevant = studentMarksheets.filter(
      (m) =>
        m.classId === currentClassId &&
        m.sessionId === currentSessionId &&
        (m.marksheetType === "ANNUAL" || m.marksheetType === "BOARD")
    );

    if (relevant.length === 0) return false;

    // Hide promotion if student has PROMOTED status in any marksheet
    const isPromoted = relevant.some((m) => m.promote === "PROMOTED");
    if (isPromoted) return false;

    return relevant.some((m) => m.promote === "NOT_PROMOTED");
  }, [isStudentRole, studentData, studentMarksheets, studentTcData]);

  if (!user) {
    return null;
  }

  // Only require schoolId for non-super-admins
  const isSuperAdmin = user.roles?.includes('ADMIN');

  if (!isSuperAdmin && !schoolId) {
    return null;
  }

  const canManageLogo = isSchoolAdmin() || isTeacher();

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file?.type.startsWith("image/")) setLogoFile(file);
    else setLogoFile(null);
  };

  const handleLogoUpload = () => {
    if (!schoolData || !logoFile) return;
    updateSchoolMutation.mutate(
      { id: schoolData.id!, schoolData: { ...schoolData, schoolLogo: logoFile.name }, imageFile: logoFile },
      {
        onSuccess: () => {
          setLogoFile(null);
          setLogoError(false);
          queryClient.invalidateQueries({ queryKey: ["schoolLogoInfo", schoolId] });
          showSuccess(t('logo_updated_successfully'));
        },
        onError: (error: any) => {
          const message = error?.response?.data?.message || t('failed_to_upload_logo');
          showError(message);
        },
      }
    );
  };

  // Helper function to detect if class is 10th or 12th
  const isExamFormClass = (className?: string | null) => {
    if (!className) return false;
    const normalized = className.toLowerCase();
    const numbers = new Set((normalized.match(/\d+/g) || []).map((num) => Number.parseInt(num, 10)));
    const hasNumber = (n: number) => numbers.has(n);
    return (
      hasNumber(10) ||
      hasNumber(12) ||
      normalized.includes('class 10') ||
      normalized.includes('class 12') ||
      normalized.includes('10th') ||
      normalized.includes('12th') ||
      normalized.includes('tenth') ||
      normalized.includes('twelfth')
    );
  };

  // Get the menu text for exam/enrollment form based on selected class
  const getExamFormMenuText = () => {
    const className = selectedClass?.name || selectedClassName || '';
    return isExamFormClass(className) ? t('exam_form') : t('enrollment_form');
  };

  // Helper function to check if class is 9th or 11th for School Register
  const isSchoolRegisterClass = (className?: string | null): boolean => {
    if (!className) return false;
    const normalized = className.toLowerCase();
    const numbers = new Set((normalized.match(/\d+/g) || []).map((num) => Number.parseInt(num, 10)));
    const hasNumber = (n: number) => numbers.has(n);
    return (
      hasNumber(9) ||
      hasNumber(11) ||
      normalized.includes('class 9') ||
      normalized.includes('class 11') ||
      normalized.includes('9th') ||
      normalized.includes('11th')
    );
  };

  const menuItems = () => {
    let menu: any[] = [];
    if (isAppUser) {
      // APP_USER role - only show application form
      menu = [
        { text: t('dashboard'), icon: <DashboardIcon />, path: '/application-form/dashboard' },
        { text: t('application_form'), icon: <ListAltIcon />, path: '/application-form' },
      ]
    } else if (isAdmin()) {
      menu = [
        // { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
        { text: t("dashboard"), icon: <DashboardIcon />, path: '/admin/dashboard' },
        { text: t('users'), icon: <PeopleIcon />, path: '/admin/users' },
        { text: t('schools'), icon: <SchoolIcon />, path: '/admin/schools' },
        { text: t('messaging_credits'), icon: <CreditScoreIcon />, path: '/admin/messaging-credits' },
      ]
    } else if (isSchoolAdmin()) {
      menu = [
        { text: t('dashboard'), icon: <DashboardIcon />, path: '/school-admin/dashboard' },
        { text: t('sessions'), icon: <DateRangeIcon />, path: '/school-admin/sessions' },
        { text: t('classes'), icon: <MenuBookIcon />, path: '/school-admin/classes' },
        { text: t('subjects'), icon: <SubjectIcon />, path: '/school-admin/subjects' },
        { text: t('houses'), icon: <HouseIcon />, path: '/school-admin/houses' },
        { text: t('application_form'), icon: <ListAltIcon />, path: '/school-admin/application-form' },
        { text: t('students'), icon: <AccessibilityNewIcon />, path: '/school-admin/students' },
        { text: t('teachers'), icon: <GroupsIcon />, path: '/school-admin/teachers' },
        { text: t('id_card'), icon: <ContactPageIcon />, path: '/school-admin/id-card' },
        { text: t('aadhar_card'), icon: <ContactPageIcon />, path: '/school-admin/aadhar-card' },
        { text: getExamFormMenuText(), icon: <AssignmentIcon />, path: '/school-admin/tenth-exam-form' },

        { text: t('enrollment'), icon: <AssessmentIcon />, path: '/school-admin/enrollment' },
        { text: t('holiday_management'), icon: <CalendarMonthIcon />, path: '/school-admin/holiday-management' },
        { text: t('attendance'), icon: <HowToRegIcon />, path: '/school-admin/attendance-management' },
        ...(schoolData?.teacherAttendanceEnabled
          ? [{ text: t('teacher_attendance', 'Teacher Attendance'), icon: <HowToRegIcon />, path: '/school-admin/teacher-attendance' }]
          : []),
        { text: 'Task Assignment', icon: <AssignmentTurnedInIcon />, path: '/principal/task-assignment' },
        { text: 'Time Table', icon: <CalendarMonthIcon />, path: '/school-admin/timetable' },
        // { text: t('fee_records'), icon: <AccountBalanceWalletIcon />, path: '/school-admin/fee-record-management' },

        {
          text: t('exam_management'),
          icon: <ExpandMoreIcon />,
          isGroup: true,
          children: [
            { text: t('roll_number'), icon: <ListAltIcon />, path: '/school-admin/roll-number' },
            { text: t('admit_cards'), icon: <CardIcon />, path: '/school-admin/admit-cards' },
            { text: t('marksheets'), icon: <CardIcon />, path: '/school-admin/create-marksheet' },
            { text: t('marksheet_template_builder'), icon: <DesignServicesIcon />, path: '/school-admin/marksheet-template-builder' },
            // Only show School Register for 9th and 11th classes
            ...(isSchoolRegisterClass(selectedClass?.name || selectedClassName)
              ? [{ text: t('school_register_management'), icon: <MenuBookIcon />, path: '/school-admin/school-register-management' }]
              : []),
            { text: t('gosvara_sheet_management'), icon: <AssessmentIcon />, path: '/school-admin/gosvara-sheet-management' },
            { text: t('merit_list'), icon: <ListAltIcon />, path: '/school-admin/merit-list-management' },
            { text: t('notice_board_list'), icon: <ListAltIcon />, path: '/school-admin/noticeboard-list-management' }
          ]
        },
        {
          text: t('certificate_management'),
          icon: <ExpandMoreIcon />,
          isGroup: true,
          children: [
            { text: t('certificates'), icon: <CardIcon />, path: '/school-admin/create-certificate' },
            { text: t('certificate_template_builder'), icon: <DesignServicesIcon />, path: '/school-admin/certificate-template-builder' }
          ]
        },
        {
          text: t('promotion'),
          icon: <ExpandMoreIcon />,
          isGroup: true,
          children: [
            { text: t('pramote_student'), icon: <AssessmentIcon />, path: '/school-admin/pramote-student' },
            { text: t('tc_list'), icon: <Description />, path: '/school-admin/tc-management' }
          ]
        },
        {
          text: t('accounts'),
          icon: <ExpandMoreIcon />,
          isGroup: true,
          children: [
            { text: t('fee_structure'), icon: <AccountBalanceWalletIcon />, path: '/school-admin/fee-structure' },
            { text: t('student_accounts'), icon: <AccountBalanceIcon />, path: '/school-admin/student-accounts' },
            { text: t('fee_receipts'), icon: <ReceiptLongIcon />, path: '/school-admin/fee-receipts' },
            { text: t('fee_transactions'), icon: <HistoryIcon />, path: '/school-admin/fee-transactions' },
          ]
        },
        { text: t('application_user'), icon: <PersonAddIcon />, path: '/school-admin/application-user' },
        { text: t('messaging'), icon: <SmsIcon />, path: '/school-admin/messaging' },
        // { text: t('actions_performed'), icon: <HistoryIcon />, path: '/school-admin/actions-performed' },

      ]
    } else if (isTeacher()) {
      menu = [
        { text: t("dashboard"), icon: <DashboardIcon />, path: '/teacher/dashboard' },
        { text: t('my_timetable', 'My Timetable'), icon: <CalendarMonthIcon />, path: '/teacher/timetable' },
        { text: t('sessions'), icon: <DateRangeIcon />, path: '/teacher/sessions' },
        { text: t('classes'), icon: <MenuBookIcon />, path: '/teacher/classes' },
        { text: t('subjects'), icon: <SubjectIcon />, path: '/teacher/subjects' },
        { text: t('homework'), icon: <EditNoteIcon />, path: '/teacher/homework' },
        { text: t('application_form'), icon: <ListAltIcon />, path: '/teacher/application-form' },
        { text: t('students'), icon: <AccessibilityNewIcon />, path: '/teacher/students' },
        { text: t('id_card'), icon: <ContactPageIcon />, path: '/teacher/id-card' },
        { text: t('aadhar_card'), icon: <ContactPageIcon />, path: '/teacher/aadhar-card' },
        { text: getExamFormMenuText(), icon: <AssignmentIcon />, path: '/teacher/tenth-exam-form' },
        { text: t('attendance'), icon: <HowToRegIcon />, path: '/teacher/attendance-management' },
        ...(schoolData?.teacherAttendanceEnabled
          ? [{ text: t('my_attendance', 'My Attendance'), icon: <HowToRegIcon />, path: '/teacher/teacher-attendance' }]
          : []),
        { text: t('enrollment'), icon: <AssessmentIcon />, path: '/teacher/enrollment' },
        // { text: t('fee_records'), icon: <AccountBalanceWalletIcon />, path: '/teacher/fee-record-management' },

        {
          text: t('exam_management'),
          icon: <ExpandMoreIcon />,
          isGroup: true,
          children: [
            { text: t('roll_number'), icon: <ListAltIcon />, path: '/teacher/roll-number' },
            { text: t('admit_cards'), icon: <CardIcon />, path: '/teacher/admit-cards' },
            { text: t('marksheets'), icon: <CardIcon />, path: '/teacher/create-marksheet' },
            { text: t('marksheet_template_builder'), icon: <DesignServicesIcon />, path: '/teacher/marksheet-template-builder' },
            { text: t('gosvara_sheet_management'), icon: <AssessmentIcon />, path: '/teacher/gosvara-sheet-management' },
            { text: t('merit_list'), icon: <ListAltIcon />, path: '/teacher/merit-list-management' },
            { text: t('notice_board_list'), icon: <ListAltIcon />, path: '/teacher/noticeboard-list-management' }
          ]
        },
        {
          text: t('certificate_management'),
          icon: <ExpandMoreIcon />,
          isGroup: true,
          children: [
            { text: t('certificates'), icon: <CardIcon />, path: '/teacher/create-certificate' },
            { text: t('certificate_template_builder'), icon: <DesignServicesIcon />, path: '/teacher/certificate-template-builder' }
          ]
        },
        {
          text: t('promotion'),
          icon: <ExpandMoreIcon />,
          isGroup: true,
          children: [
            { text: t('pramote_student'), icon: <AssessmentIcon />, path: '/teacher/pramote-student' },
            { text: t('tc_list'), icon: <Description />, path: '/teacher/tc-management' }
          ]
        },
        {
          text: t('accounts'),
          icon: <ExpandMoreIcon />,
          isGroup: true,
          children: [
            { text: t('student_accounts'), icon: <AccountBalanceIcon />, path: '/teacher/student-accounts' },
            { text: t('fee_receipts'), icon: <ReceiptLongIcon />, path: '/teacher/fee-receipts' },
            { text: t('fee_transactions'), icon: <HistoryIcon />, path: '/teacher/fee-transactions' },
          ]
        },
        { text: 'Student Update Requests', icon: <AssignmentIcon />, path: '/teacher/student-update-requests' },
        { text: 'My Tasks', icon: <AssignmentTurnedInIcon />, path: '/teacher/my-tasks' },

      ]
    } else if (isStudent()) {
      menu = [
        { text: t('dashboard'), icon: <DashboardIcon />, path: '/student/dashboard' },
        { text: t('my_timetable', 'My Timetable'), icon: <CalendarMonthIcon />, path: '/student/timetable' },
        { text: t('my_homework', 'My Homework'), icon: <EditNoteIcon />, path: '/student/homework' },
        { text: t('profile_management'), icon: <AccountIcon />, path: '/student/profile' },
        // Only show fee payments if the service is enabled in school settings
        ...(schoolData?.feePaymentService ? [{ text: t('fee_payments'), icon: <AccountBalanceWalletIcon />, path: '/student/fee-payment' }] : []),
        { text: t('admit_cards'), icon: <CardIcon />, path: '/student/admit-cards' },
        { text: t('aadhar_card'), icon: <ContactPageIcon />, path: '/student/aadhar-card' },
        { text: getExamFormMenuText(), icon: <AssignmentIcon />, path: '/student/exam-form' },
        {
          text: t('results'),
          icon: <ExpandMoreIcon />,
          isGroup: true,
          children: [
            { text: t('marksheets'), icon: <CardIcon />, path: '/student/marksheet-marks' },
            { text: t('merit_list'), icon: <ListAltIcon />, path: '/student/merit-list' },
            { text: t('notice_board_list'), icon: <ArticleIcon />, path: '/student/noticeboard-list' }
          ]
        },
        ...(shouldShowStudentPromotion
          ? [{ text: t('promotion'), icon: <Description />, path: '/student/promotion' }]
          : []),
        { text: t('tc_list'), icon: <Description />, path: '/student/tc-list' },
        { text: 'Student Update Requests', icon: <AssignmentIcon />, path: '/student/student-update-requests' },
      ]
    }
    return menu;
  }


  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(180deg, #f8fafe 0%, #ffffff 100%)'
    }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg,rgb(50, 140, 249) 0%,rgb(50, 140, 249) 100%)',
          color: 'white',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '20px',
            background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 100%)',
          }
        }}
      >
        {logoLoading ? (
          <Box
            sx={{
              width: 130,
              height: 130,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid rgba(255,255,255,0.3)',
              backgroundColor: 'rgba(255,255,255,0.15)',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.7)' }}
            >
              Loading...
            </Typography>
          </Box>
        ) : (
          <>
          <Box sx={{ position: 'relative', width: 130, height: 130 }}>
            {/* Main circular logo */}
            <Box
              sx={{
                width: 130,
                height: 130,
                borderRadius: '50%',
                overflow: 'hidden',
                border: '3px solid #ffffff',
                backgroundColor: 'rgb(50, 140, 249)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {updateSchoolMutation.isLoading ? (
                <CircularProgress size={36} sx={{ color: 'rgba(255,255,255,0.9)' }} />
              ) : !!logoInfo?.fileName && !logoError ? (
                <img
                  src={`${apiBaseUrl}/schools/logos/${logoInfo.fileName}`}
                  alt="School Logo"
                  onError={() => setLogoError(true)}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, textAlign: 'center', px: 1 }}
                >
                  School Logo
                </Typography>
              )}
            </Box>

            {/* Bottom-right corner camera button */}
            {canManageLogo && !updateSchoolMutation.isLoading && (
              <Box
                component="label"
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                  border: '2px solid #ffffff',
                  '&:hover': { bgcolor: '#f0f0f0' },
                  transition: 'background 0.2s',
                }}
              >
                <CameraAltIcon sx={{ fontSize: '0.95rem', color: '#333333' }} />
                <input
                  ref={logoInputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoFileChange}
                />
              </Box>
            )}
          </Box>

          {/* Upload / Cancel buttons after file selected */}
          {canManageLogo && logoFile && !updateSchoolMutation.isLoading && (
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleLogoUpload}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.65rem',
                  py: 0.4,
                  px: 1.5,
                  bgcolor: '#fff',
                  color: 'rgb(50, 140, 249)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.88)' },
                  borderRadius: 5,
                  boxShadow: 'none',
                  minWidth: 0,
                  fontWeight: 700,
                }}
              >
                Upload
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => setLogoFile(null)}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.65rem',
                  py: 0.4,
                  px: 1,
                  color: 'rgba(255,255,255,0.75)',
                  minWidth: 0,
                  '&:hover': { color: '#fff', bgcolor: 'transparent' },
                }}
              >
                Cancel
              </Button>
            </Box>
          )}
          </>
        )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: '1rem',
            textAlign: 'center',
            opacity: 0.9
          }}
        >
          iSchoolNext
        </Typography>
        <Typography
          variant="caption"
          sx={{
            opacity: 0.7,
            textAlign: 'center',
            fontSize: '0.75rem'
          }}
        >
          Management System
        </Typography>
      </Box>
      {/* <List sx={{ flex: 1 }}> */}
      <List sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        '&&::-webkit-scrollbar': { width: 0, height: 0 },
        '&&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent' },
        px: 1,
        py: 2
      }}>
        {/* {menuItems().map((item) => (
                    <ListItem
                        button
                        key={item.text}
                        onClick={() => {
                            navigate(item.path)
                            if (isMobile) handleDrawerToggle()
                        }}
                        selected={location.pathname === item.path}
                        sx={{
                            '&.Mui-selected': {
                                bgcolor: '#3092e3',
                                color: 'white',
                                '& .MuiListItemIcon-root': {
                                    color: 'white',
                                },
                            },
                        }}
                    >
                        <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                    </ListItem>
                ))} */}
        {menuItems().map((item) => {
          if (item.isGroup) {
            // Determine which state to use based on the group name
            const isMarksheetManagement = item.text === t('exam_management');
            const isResultSheet = item.text === t('result_sheet');
             const isPromotion = item.text === t('promotion');
            const isResults = item.text === t('results');
            const isAccounts = item.text === t('accounts');
            const isCertificateManagement = item.text === t('certificate_management');
            const isOpen = isMarksheetManagement ? resultSheetOpen : isResultSheet ? resultSheetOpen : isPromotion ? promotionOpen : isResults ? resultsOpen : isAccounts ? accountsOpen : isCertificateManagement ? certificateOpen : false;
            const setIsOpen = isMarksheetManagement ? setResultSheetOpen : isResultSheet ? setResultSheetOpen : isPromotion ? setPromotionOpen : isResults ? setResultsOpen : isAccounts ? setAccountsOpen : isCertificateManagement ? setCertificateOpen : () => { };

            return (
              <Box key={item.text}>
                <ListItem
                  button
                  onClick={() => setIsOpen(!isOpen)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    mx: 0.5,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(1, 104, 234, 0.08)',
                      transform: 'translateX(4px)',
                    },
                    '&.Mui-selected': {
                      bgcolor: '#3092e3',
                      color: 'white',
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child: any) => (
                      <ListItem
                        button
                        key={child.text}
                        onClick={() => {
                          // Marksheet Management: refresh marksheets on entry, and prefetch immediately (priority)
                          if (
                            child.path === "/school-admin/create-marksheet" ||
                            child.path === "/teacher/create-marksheet"
                          ) {
                            queryClient.invalidateQueries({ queryKey: ["marksheets"] });
                            const prefetchClassId =
                              selectedClassIdFromLayout ?? selectedClass?.id;
                            const prefetchSessionId =
                              selectedSessionIdFromLayout ?? selectedSession?.id;
                            if (
                              prefetchClassId != null &&
                              prefetchSessionId != null &&
                              prefetchClassId > 0 &&
                              prefetchSessionId > 0
                            ) {
                              const classId = prefetchClassId;
                              const sessionId = prefetchSessionId;
                              // Warm the same paged query as Marksheet Management (avoid prefetching with size=200).
                              queryClient.prefetchQuery({
                                queryKey: [
                                  "marksheetsPage",
                                  classId,
                                  null,
                                  sessionId,
                                  0,
                                  10,
                                ],
                                queryFn: () =>
                                  getMarksheetsPageByClassKeyAndSessionId({
                                    classId,
                                    sessionId,
                                    page: 0,
                                    size: 10,
                                  }),
                              });
                            }
                          }
                          navigate(child.path)
                          if (isMobile) handleDrawerToggle()
                        }}
                        selected={location.pathname === child.path}
                        sx={{
                          pl: 3,
                          ml: 2,
                          mr: 0.5,
                          mb: 0.5,
                          borderRadius: 2,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            bgcolor: 'rgba(1, 104, 234, 0.05)',
                            transform: 'translateX(2px)',
                          },
                          '&.Mui-selected': {
                            bgcolor: 'rgba(59, 162, 245, 0.15)',
                            color: '#3092e3',
                            borderLeft: '3px solid #3092e3',
                            '& .MuiListItemIcon-root': {
                              color: '#3092e3',
                            },
                            '&:hover': {
                              bgcolor: 'rgba(25, 118, 210, 0.25)',
                              color: '#1976d2',
                              borderLeft: '3px solid #1976d2',
                              '& .MuiListItemIcon-root': {
                                color: '#1976d2',
                              },
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: 'inherit' }}>{child.icon}</ListItemIcon>
                        <ListItemText primary={child.text} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            )
          } else {
            return (
              <ListItem
                button
                key={item.text}
                onClick={() => {
                  navigate(item.path)
                  if (isMobile) handleDrawerToggle()
                }}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  mx: 0.5,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'rgba(1, 104, 234, 0.08)',
                    transform: 'translateX(4px)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#3092e3',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(59, 162, 245, 0.3)',
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '&:hover': {
                      backgroundColor: '#1976d2',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            )
          }
        })}

      </List>
    </Box>
  )


  return (
    <Box
      component="nav"
      sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
    >
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            borderRight: 'none',
            boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            height: 'calc(100vh - 64px)', // Subtract AppBar height (typically 64px)
            top: '64px', // Position below AppBar
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.3s ease-in-out',
            transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
            position: 'fixed',
            zIndex: (theme) => theme.zIndex.drawer,
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}


